import { SelectField, type SubmitHandler } from '@ff-it/form';
import { Alert, type RenderProps } from '@ff-it/ui';
import { BlockWidget, ButtonGroupField, DialogForm, useOptions } from 'components';
import { PeriodWidget } from 'components/PeriodWidget';
import type { ListBlock } from 'modules/campaign/block/types';
import { type ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { useField, useForm } from 'react-final-form';
import { api } from 'services';
import type { ActivityType } from 'types';
import type { PlanMeta } from '../state';
import type { PlanResponse } from '../types';
import * as styles from './ImportBlockDialog.css';
import { analyzeSchedule } from './scheduleAnalysis';
import { ScheduleModeField, type ScheduleTransferMode } from './ScheduleModeField';
import { ScheduleTimeline } from './ScheduleTimeline';

type PlanImportMode = 'append' | 'replace';

export type ImportBlockValues = {
  block: ListBlock | null;
  source_revision: number | null;
  plan: PlanImportMode;
  schedule: ScheduleTransferMode;
};

type ImportBlockProps = {
  type: ActivityType;
  blockId: number;
  destination: PlanMeta;
  hasPlanContent: boolean;
  hasLockedRows: boolean;
};

type SourceLoadState =
  | { status: 'idle' }
  | { status: 'loading'; blockId: number }
  | { status: 'error'; blockId: number }
  | { status: 'success'; blockId: number; plan: PlanResponse };

function rowCountLabel(count: number): string {
  return `${count} ${count === 1 ? 'row' : 'rows'}`;
}

function ImportableBlockField({ blockId, type }: Pick<ImportBlockProps, 'blockId' | 'type'>): ReactElement {
  const loadOptions = useOptions<ListBlock>('campaign/blocks/', { type, exclude: blockId });

  return (
    <SelectField<ListBlock>
      autoFocus
      className="mb-0"
      loadOptions={loadOptions}
      getOptionLabel={({ code, title }) => `${title} ${code}`}
      getOptionValue={({ id }) => id.toString()}
      formatOptionLabel={BlockWidget}
      name="block"
      placeholder="Choose a block to import…"
      required
    />
  );
}

function PlanModeField({
  rowCount,
  hasPlanContent,
  hasLockedRows,
}: {
  rowCount: number | null;
  hasPlanContent: boolean;
  hasLockedRows: boolean;
}): ReactElement {
  const {
    input: { value },
  } = useField<PlanImportMode>('plan', { subscription: { value: true } });
  const importedRows = rowCount === null ? 'the source rows' : rowCountLabel(rowCount);

  let help = `Adds ${importedRows} to the current plan. Existing rows are kept.`;
  if (value === 'replace') {
    help = hasPlanContent
      ? `Removes current rows, groups, factors, and schedule before importing ${importedRows}.`
      : `Imports ${importedRows} into the empty plan.`;
  } else if (hasLockedRows) {
    help += ' Replace is unavailable while destination rows are locked or externally linked.';
  }

  return (
    <ButtonGroupField
      className="mb-0"
      name="plan"
      label="Plan"
      variant="secondary"
      fullWidth
      options={[
        { value: 'append', label: 'Append' },
        { value: 'replace', label: 'Replace', disabled: hasLockedRows },
      ]}
      help={help}
    />
  );
}

function ImportOptions({
  blockId,
  type,
  destination,
  hasPlanContent,
  hasLockedRows,
  onSourceLoadingChange,
  onSourceRowCountChange,
}: ImportBlockProps & {
  onSourceLoadingChange: (loading: boolean) => void;
  onSourceRowCountChange: (rowCount: number | null) => void;
}): ReactElement {
  const form = useForm<ImportBlockValues>();
  const {
    input: { value: source },
  } = useField<ListBlock | null>('block', { subscription: { value: true }, allowNull: true });
  const [loadState, setLoadState] = useState<SourceLoadState>({ status: 'idle' });
  const requestVersion = useRef(0);

  useEffect(() => {
    const version = ++requestVersion.current;
    onSourceLoadingChange(source !== null);
    onSourceRowCountChange(null);
    form.batch(() => {
      form.change('source_revision', null);
      form.change('plan', 'append');
      form.change('schedule', 'skip');
    });

    if (!source) {
      setLoadState({ status: 'idle' });
      return;
    }

    const selectedSource = source;
    const controller = new AbortController();
    setLoadState({ status: 'loading', blockId: source.id });

    void (async () => {
      try {
        const result = await api.request<PlanResponse, unknown>({
          method: 'GET',
          url: `campaign/blocks/${selectedSource.id}/plan/`,
          abortController: controller,
        });
        if (requestVersion.current !== version) return;
        if (!result.ok) {
          setLoadState({ status: 'error', blockId: selectedSource.id });
          onSourceLoadingChange(false);
          return;
        }

        const sourceRevision = Number(result.response.headers.get('X-Revision'));
        if (!Number.isInteger(sourceRevision)) {
          setLoadState({ status: 'error', blockId: selectedSource.id });
          onSourceLoadingChange(false);
          return;
        }
        form.change('source_revision', sourceRevision);
        setLoadState({ status: 'success', blockId: selectedSource.id, plan: result.data });
        onSourceRowCountChange(result.data.rows.length);
        onSourceLoadingChange(false);
      } catch {
        if (requestVersion.current !== version) return;
        setLoadState({ status: 'error', blockId: selectedSource.id });
        onSourceLoadingChange(false);
      }
    })();

    return () => {
      controller.abort();
      if (requestVersion.current === version) requestVersion.current++;
    };
  }, [form, onSourceLoadingChange, onSourceRowCountChange, source]);

  const sourcePlan = loadState.status === 'success' && loadState.blockId === source?.id ? loadState.plan : null;
  const analysis = useMemo(
    () => (sourcePlan ? analyzeSchedule(sourcePlan, destination) : null),
    [sourcePlan, destination],
  );

  return (
    <div className={styles.content}>
      <div className={styles.sourceSection}>
        <ImportableBlockField blockId={blockId} type={type} />
        {source && (
          <div className={styles.sourceMeta}>
            {sourcePlan
              ? rowCountLabel(sourcePlan.rows.length)
              : loadState.status === 'error'
                ? 'Rows unavailable'
                : 'Loading rows'}{' '}
            · block period <PeriodWidget date_from={source.date_from} date_to={source.date_to} />
          </div>
        )}
      </div>
      {source && (
        <>
          {source.type !== type && <Alert variant="danger">Source and destination block types must match.</Alert>}
          {loadState.status === 'error' && (
            <Alert variant="danger">Unable to load the source plan. Select the block again to retry.</Alert>
          )}
          {sourcePlan && analysis && <ScheduleTimeline analysis={analysis} destination={destination} />}
          <PlanModeField
            rowCount={analysis?.rowCount ?? null}
            hasPlanContent={hasPlanContent}
            hasLockedRows={hasLockedRows}
          />
          <ScheduleModeField status={loadState.status} analysis={analysis} destination={destination} />
        </>
      )}
    </div>
  );
}

export function ImportBlockDialog({
  blockId,
  type,
  destination,
  hasPlanContent,
  hasLockedRows,
  ...props
}: ImportBlockProps & {
  submitHandler: SubmitHandler<ImportBlockValues>;
} & RenderProps<undefined>) {
  const [sourceLoading, setSourceLoading] = useState(false);
  const [sourceRowCount, setSourceRowCount] = useState<number | null>(null);
  const submitLabel = sourceRowCount === null ? 'Import rows' : `Import ${rowCountLabel(sourceRowCount)}`;

  return (
    <DialogForm
      {...props}
      title="Import block"
      initialValues={{ block: null, source_revision: null, plan: 'append', schedule: 'skip' }}
      disabled={sourceLoading}
      submitLabel={submitLabel}
      validate={({ block, source_revision }: ImportBlockValues) => {
        const errors: Partial<Record<keyof ImportBlockValues, string>> = {};
        if (!block) {
          errors.block = 'Select a block to import.';
        } else if (block.type !== type) {
          errors.block = 'Source and destination block types must match.';
        }
        if (block && source_revision === null) {
          errors.source_revision = 'Source plan must load before import.';
        }
        return Object.keys(errors).length ? errors : undefined;
      }}
    >
      <ImportOptions
        blockId={blockId}
        type={type}
        destination={destination}
        hasPlanContent={hasPlanContent}
        hasLockedRows={hasLockedRows}
        onSourceLoadingChange={setSourceLoading}
        onSourceRowCountChange={setSourceRowCount}
      />
    </DialogForm>
  );
}
